import React from 'react';
import type { MatchState, Side } from '../../../../services/tennis/scoring';
import { TennisBall } from './TennisBall';

interface ScoreBoardProps {
  state: MatchState;
  ourName: string;
  opponentName: string;
}

/**
 * Set-by-set scoreboard. Game points live on the big point pads, so this only
 * shows sets — one column per possible set, unplayed ones dashed. Rows are
 * ordered opponent-then-us to mirror the pads below (opponent on top, our
 * side nearest the thumb).
 */
export const ScoreBoard: React.FC<ScoreBoardProps> = ({ state, ourName, opponentName }) => {
  const { format } = state;
  const totalSets = format.sets * 2 - 1;
  const hasMatchTiebreakDecider = format.sets > 1 && format.decidingSet === 'match_tiebreak';
  const columns = Array.from({ length: totalSets }, (_, i) => i);

  const cell = (side: Side, index: number) => {
    const key = side === 'us' ? 'ourGames' : 'theirGames';
    const otherKey = side === 'us' ? 'theirGames' : 'ourGames';
    const done = state.sets[index];
    if (done) {
      const value = done.isMatchTiebreak && done.tiebreak ? (side === 'us' ? done.tiebreak.ours : done.tiebreak.theirs) : done[key];
      const won = done[key] > done[otherKey];
      return (
        <span className={won ? 'font-bold text-gray-900 dark:text-gray-50' : 'text-gray-400 dark:text-gray-500'}>{value}</span>
      );
    }
    if (index === state.sets.length && state.currentSet) {
      return <span className="font-bold text-primary">{state.currentSet[key]}</span>;
    }
    return <span className="text-gray-300 dark:text-gray-600">–</span>;
  };

  const row = (side: Side, name: string) => (
    <div className="flex items-center h-9 border-t border-gray-100 dark:border-gray-800">
      <div className="flex-1 min-w-0 pl-3 flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-gray-100">
        <span className={state.server === side && !state.winner ? '' : 'invisible'}>
          <TennisBall />
        </span>
        <span className="truncate">{name}</span>
      </div>
      {columns.map((i) => (
        <div key={i} className="w-8 text-center text-base tabular-nums">
          {cell(side, i)}
        </div>
      ))}
      <div className="w-2" />
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center h-5 pt-1 text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500">
        <div className="flex-1" />
        {columns.map((i) => (
          <div key={i} className="w-8 text-center">
            {hasMatchTiebreakDecider && i === totalSets - 1 ? 'TB' : `S${i + 1}`}
          </div>
        ))}
        <div className="w-2" />
      </div>
      {row('them', opponentName)}
      {row('us', ourName)}
    </div>
  );
};

export default ScoreBoard;
